'use client'

import { clsx } from 'clsx'
import { UsersRound } from 'lucide-react'
import * as React from 'react'
import { useCallback } from 'react'
import toast from 'react-hot-toast'

import { useInfoDialog } from '@/components/info-dialog'
import MarkdownView from '@/components/markdown-view'
import { Button } from '@/components/ui/button'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import {
  CompetitionStatus,
  getCompetitionStatus,
  getIsInApplicationDuration,
} from '@/constants/competition-status'
import {
  ApplicantsFull,
  NotEligible,
} from '@/constants/error-code-text'
import useLogin from '@/hooks/use-login'
import {
  formatNumber,
  formatUTCTime,
} from '@/lib/formatter'
import { isApiError } from '@/lib/utils'
import {
  useApplyCompetitionMutation,
  useReadOneCompetitionQuery,
} from '@/redux/competition'
import { useAppSelector } from '@/redux/utils'

export interface ContentProps {
  id: number
  isApplied: boolean
  refetchMineUser: () => void
}

export default function Content({ id, isApplied, refetchMineUser }: ContentProps) {
  const { data: competition } = useReadOneCompetitionQuery(id)
  const [apply, { isLoading: isApplying, isSuccess: isJustApplied }] = useApplyCompetitionMutation()
  const token = useAppSelector((state) => state.global.token)
  const login = useLogin()
  const info = useInfoDialog()
  const onApply = useCallback(async () => {
    try {
      if (!token) {
        login()
        return
      }
      await apply(id).unwrap()
      refetchMineUser()
      toast.success('Successfully joined the event')
    } catch (error) {
      if (isApiError(error)) {
        const { data: { codeText, message } } = error
        if (codeText === ApplicantsFull) {
          info({
            title: 'Sorry, applicants full',
            description: 'Sorry, this event has reached the maximum number of applicants.',
          })
          return
        }
        if (codeText === NotEligible) {
          info({
            title: 'Sorry，you\'re not eligible to apply',
            description: 'Please review the event details carefully.You may need to meet specific requirements before joining.Make sure you\'ve completed all the necessary steps.',
          })
          return
        }
        info({
          title: 'Apply failed',
          description: message,
        })
      }
    }
  }, [id, info, login, apply, refetchMineUser, token])

  if (!competition) return null

  const {
    title,
    applicantCount,
    author,
    publishAt,
    description,
    note,
    rewardAt,
    overview,
    howToParticipate,
    applicantLimit,
    applyStartAt,
    applyEndAt,
    competitionStartAt,
    competitionEndAt,
  } = competition
  const status = getCompetitionStatus(competition)
  const [applyText, disabled, tooltipText] = (() => {
    const isFinalApplied = isApplied || isJustApplied
    const isFull = applicantCount >= applicantLimit
    const isInParticipateDuration = getIsInApplicationDuration(competition)

    if (isFinalApplied) return ['Already Applied', true]
    if (!isInParticipateDuration) return ['Coming Soon', true, 'This event hasn’t started yet. Stay tuned!']
    if (isApplying) return ['Applying...', true]
    if (isFull) return ['Apply (Full)', false]
    return ['Apply', false]
  })()

  return (
    <div className="w-(--content-width) mx-auto">
      <div className="flex justify-between items-center">
        <div className="text-4xl font-semibold font-urbanist">
          {title}
        </div>
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                onClick={onApply}
                size="xl"
                variant="primary"
                disabled={disabled}
                isLoading={isApplying}
                className="px-12"
              >
                {applyText}
              </Button>
            </TooltipTrigger>
            {tooltipText && (
              <TooltipContent withArrow>
                <p>{tooltipText}</p>
              </TooltipContent>
            )}
          </Tooltip>
        </TooltipProvider>

      </div>
      <div className="flex gap-4 mt-8">
        <div
          className={clsx(
            status === CompetitionStatus.OnProgress && 'bg-linear-to-r from-primary to-gradient-end',
            status === CompetitionStatus.Ended && 'text-muted-foreground',
            'text-xs px-2 py-1 rounded-sm bg-white/12',
          )}
        >
          {status.toString()}
        </div>
        <div className="flex gap-1 text-xs px-2 py-1 bg-white/12 rounded-sm items-center">
          <UsersRound className="size-3" />
          <div>{formatNumber()(applicantCount)}</div>
          <div>Applied</div>
        </div>
      </div>
      <div className="text-white/50 mt-4 italic text-sm flex gap-6">
        <div>Author: {author}</div>
        <div>Published on: (UTC) {formatUTCTime('MMMM DD, YYYY')(publishAt)}</div>
      </div>
      <div className="grid gap-12 mt-16">
        <div>
          <div className="whitespace-pre-wrap">
            {description}
          </div>
          <p className="mt-6 text-secondary-foreground text-sm whitespace-pre-wrap">
            📢 Note: {note}
          </p>
        </div>
        <div>
          <div className="text-xl font-semibold mb-4">
            🗓 Event Timeline
          </div>
          <ul className="grid gap-4 list-[square] list-inside">
            <li>Application: (UTC) {formatUTCTime('HH:mm MMMM DD')(applyStartAt)} - {formatUTCTime('HH:mm MMMM DD')(applyEndAt)}</li>
            <li>Competition: (UTC) {formatUTCTime('HH:mm MMMM DD')(competitionStartAt)} – {formatUTCTime('HH:mm MMMM DD')(competitionEndAt)}</li>
            <li>Final Results & Rewards Distribution: (UTC) {formatUTCTime('HH:mm MMMM DD, YYYY')(rewardAt)}</li>
          </ul>
        </div>
        <div>
          <div className="text-xl font-semibold mb-4">
            💡 Event Overview
          </div>
          <MarkdownView markdown={overview} />
        </div>
        <div>
          <div className="text-xl font-semibold mb-4">
            📝 How to Participate
          </div>
          <MarkdownView markdown={howToParticipate} />
        </div>
      </div>
    </div>
  )
}
